import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactPlayer from 'react-player';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import * as htmlToImage from 'html-to-image';
// Lucide icons import
import {
    Upload,
    FileText,
    FileAudio,
    Play,
    Pause,
    PlusCircle,
    Sparkles,
    Pencil,
    Clock,
    Trash2,
    Download,
} from 'lucide-react';

const msToTimestamp = (ms) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const h = Math.floor(m / 60);
    const remainderS = s % 60;
    const remainderM = m % 60;
    const remainderMs = Math.floor(ms % 1000);
    return `${String(h).padStart(2, '0')}:${String(remainderM).padStart(2, '0')}:${String(remainderS).padStart(2, '0')}.${String(remainderMs).padStart(3, '0')}`;
};

const adTxtTimestampToMs = (ts) => {
    ts = ts.trim();
    try {
        let h, m, s, ms;

        if (ts.includes(".") && ts.split(".").length - 1 === 3) {
            [h, m, s, ms] = ts.split(".");
        } else if (ts.includes(":")) {
            const parts = ts.split(":");

            if (parts.length === 4) {
                h = parts[0];
                m = parts[1];
                s = parts[2];
                ms = parts[3];
            } else if (parts.length === 3 && parts[2].includes(".")) {
                h = parts[0];
                m = parts[1];
                const rest = parts[2];

                const restParts = rest.split(".");
                if (restParts.length < 2) {
                    throw new Error("Invalid timestamp format");
                }
                s = restParts[0];
                ms = restParts[1];
            } else {
                throw new Error("Invalid timestamp format");
            }
        } else {
            throw new Error("Invalid timestamp format");
        }

        return (parseInt(h) * 3600 + parseInt(m) * 60 + parseInt(s)) * 1000 + parseInt(ms);
    } catch (error) {
        console.log(`[AD] Invalid timestamp format: ${ts}`);
        return 0;
    }
};

const AnnotationDialog = ({ timestamp, initialText = '', rowIndex = null, onClose, onSave, isAIModal = false, isLoading = false, error = null }) => {
    const [annotationText, setAnnotationText] = useState(initialText);

    useEffect(() => {
        setAnnotationText(initialText);
    }, [initialText]);

    const handleSave = () => {
        onSave(annotationText, rowIndex);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl">
                <h2 className="text-xl font-bold mb-4">{rowIndex !== null ? 'Edit Annotation' : (isAIModal ? 'AI Generated Annotation' : 'Add Annotation')}</h2>
                {rowIndex !== null && <p className="mb-2">Editing Annotation #{rowIndex + 1}</p>}
                <p className="mb-4">Timestamp: {timestamp}</p>
                {isLoading ? (
                    <div className="flex items-center justify-center h-40">
                        <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-700">Fetching AI description...</p>
                    </div>
                ) : error ? (
                    <div className="text-red-500 mb-4">{error}</div>
                ) : (
                    <textarea
                        className="w-full p-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="10"
                        value={annotationText}
                        onChange={(e) => setAnnotationText(e.target.value)}
                    ></textarea>
                )}
                <div className="flex justify-end space-x-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                        disabled={isLoading || error}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};


const VideoAnnotator = () => {
    const [videoUrl, setVideoUrl] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTimeMs, setCurrentTimeMs] = useState(0);
    const [durationMs, setDurationMs] = useState(0);
    const [annotations, setAnnotations] = useState([]);
    const [captions, setCaptions] = useState([]);
    const [currentCaption, setCurrentCaption] = useState('');
    const [showAnnotationDialog, setShowAnnotationDialog] = useState(false);
    const [selectedAnnotationRow, setSelectedAnnotationRow] = useState(null);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [annotationToEdit, setAnnotationToEdit] = useState(null);
    const playerRef = useRef(null);
    const videoContainerRef = useRef(null); // Ref for the video container
    const [showAIModal, setShowAIModal] = useState(false); // State for AI modal
    const [chatGptResponse, setChatGptResponse] = useState(''); // State to store ChatGPT response
    const [isLoadingAI, setIsLoadingAI] = useState(false); // Loading state for AI call
    const [aiError, setAiError] = useState(null); // Error state for AI call
    const [videoFileName, setVideoFileName] = useState('');


    const { userLoggedIn } = useAuth();
    const navigate = useNavigate();

    const openAiChat = async (imageData) => {
        console.log("Sending image data to OpenAI for description...");
        console.log("Image Data:", imageData); // Log the image data for debugging
        setIsLoadingAI(true);
        setAiError(null);
        try {
            const response = await fetch('https://api.openai.com/v1/chat/completions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}` // Ensure you have this env variable
                },
                body: JSON.stringify({
                    model: "gpt-4o-mini", // Or another suitable model for vision like 'gpt-4-vision-preview'
                    messages: [
                        {
                            role: "user",
                            content: [
                                {
                                    type: "text",
                                    // text: "Generate a 2 liner audio description for this image. Include descriptions of:\n- **Characters:** Who are they, what are they doing, their expressions, and their general appearance.\n- **Setting/Environment:** Where is the scene taking place? Describe key objects, background, and lighting.\n- **Actions:** What primary actions are occurring?\n- **Emotion/Mood:** What is the overall tone or feeling conveyed by the image?\n- **Important Visual Cues:** Any text, symbols, or significant non-verbal cues.\n\nFormat the description as a narrative, suitable for an audio track."
                                    text: "Generate a concise audio description for this image, focusing on:\n- Characters: Gender, action, appearance.\n- Setting: Location.\n- Actions: Primary activities.\n- Visual Cues: Any text or symbols.\n\nFormat as a brief narrative."
                                },
                                {
                                    type: "image_url",
                                    image_url: {
                                        url: imageData,
                                    },
                                },
                            ],
                        },
                    ],
                    max_tokens: 100,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.choices[0].message.content;
        } catch (error) {
            console.error("Error communicating with ChatGPT:", error);
            setAiError(`Failed to fetch AI description: ${error.message}`);
            return "Error: Could not fetch AI description.";
        } finally {
            setIsLoadingAI(false);
        }
    };


    const handleLoadVideo = (event) => {
        const file = event.target.files[0];
        if (file) {
            setVideoUrl(URL.createObjectURL(file));
            setVideoFileName(file.name);
            setIsPlaying(true);
            setAnnotations([]);
            setCaptions([]);
            setCurrentCaption('');
        }
    };

    const handleProgress = useCallback((state) => {
        setCurrentTimeMs(state.playedSeconds * 1000);
        setProgress(state.played);
    }, []);

    useEffect(() => {
        if (!userLoggedIn) {
            navigate('/login');
        }
    }, [userLoggedIn, navigate]);

    useEffect(() => {
        let caption = '';
        for (const { start, end, text } of captions) {
            if (currentTimeMs >= start && currentTimeMs <= end) {
                caption = text;
                break;
            }
        }
        setCurrentCaption(caption);
    }, [currentTimeMs, captions]);

    const handleTogglePlayback = () => {
        setIsPlaying(!isPlaying);
    };

    const handleSliderChange = (event) => {
        if (playerRef.current && durationMs > 0) {
            const newProgress = parseFloat(event.target.value) / 1000;
            playerRef.current.seekTo(newProgress, 'fraction');
            setProgress(newProgress);
            setCurrentTimeMs(newProgress * durationMs);
        }
    };

    const handleAddAnnotation = () => {
        setAnnotationToEdit(null);
        setChatGptResponse(''); // Clear previous AI response
        setAiError(null);
        setShowAnnotationDialog(true);
    };

    const handleEditAnnotationText = () => {
        if (selectedAnnotationRow !== null) {
            setAnnotationToEdit(annotations[selectedAnnotationRow]);
            setChatGptResponse(''); // Clear previous AI response
            setAiError(null);
            setShowAnnotationDialog(true);
        }
    };

    const handleSaveAnnotation = (annotationText, rowIndex) => {
        let updatedAnnotations;
        if (rowIndex !== null) {
            updatedAnnotations = [...annotations];
            updatedAnnotations[rowIndex].text = annotationText;
        } else {
            updatedAnnotations = [...annotations, { timestamp: msToTimestamp(currentTimeMs), text: annotationText }];
        }

        // Sort annotations by timestamp
        updatedAnnotations.sort((a, b) => {
            const timeA = adTxtTimestampToMs(a.timestamp);
            const timeB = adTxtTimestampToMs(b.timestamp);
            return timeA - timeB;
        });

        setAnnotations(updatedAnnotations);
        setShowAnnotationDialog(false);
        setAnnotationToEdit(null);
        setShowAIModal(false); // Close AI modal if open
        setChatGptResponse(''); // Clear response after saving
    };

    const handleSaveToTxt = () => {
        let textContent = '';
        annotations.forEach((ann, index) => {
            const formattedTimestamp = ann.timestamp.split('.')[0] + ':' + ann.timestamp.split('.')[1];
            textContent += `Edit ${index + 1}\n`;
            textContent += `Speaker 2\n`;
            textContent += `${formattedTimestamp} – ${ann.text}\n\n`;
        });

        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        let filename = 'annotations_ad.txt';
        if (videoFileName) {
            const baseName = videoFileName.replace(/\.[^/.]+$/, ""); // Remove extension
            filename = `${baseName}_AD.txt`;
        }

        const a = document.createElement('a');
        a.href = url;
        a.download = filename
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleDeleteSelectedRow = () => {
        if (selectedAnnotationRow !== null) {
            const newAnnotations = annotations.filter((_, index) => index !== selectedAnnotationRow);
            setAnnotations(newAnnotations);
            setSelectedAnnotationRow(null);
        }
    };

    const handleLoadSrt = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const srtContent = e.target.result;
                try {
                    const parsedCaptions = srtContent.split(/\r?\n\r?\n/).map(block => {
                        const lines = block.split(/\r?\n/);
                        if (lines.length >= 3) {
                            const timeString = lines[1];
                            const text = lines.slice(2).join(' ').replace(/<\/?[^>]+(>|$)/g, "");
                            const [startTimeStr, endTimeStr] = timeString.split(' --> ');

                            const parseTime = (time) => {
                                const [h, m, sMs] = time.split(':');
                                const [s, ms] = sMs.split(',');
                                return (parseInt(h, 10) * 3600 + parseInt(m, 10) * 60 + parseInt(s, 10)) * 1000 + parseInt(ms, 10);
                            };

                            return {
                                start: parseTime(startTimeStr),
                                end: parseTime(endTimeStr),
                                text: text.trim()
                            };
                        }
                        return null;
                    }).filter(Boolean);
                    setCaptions(parsedCaptions);
                } catch (error) {
                    console.error("Error parsing SRT:", error);
                    console.error("Error parsing SRT file. Please ensure it's a valid SRT format.");
                }
            };
            reader.readAsText(file);
        }
    };

    const handleLoadAdFile = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const adContent = e.target.result;
                const lines = adContent.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
                const newAnnotations = [];
                let i = 0;
                while (i < lines.length) {
                    if (lines[i].startsWith("Edit") && i + 2 < lines.length && lines[i + 2].includes("–")) {
                        const raw = lines[i + 2];
                        const [ts_raw, text] = raw.split("–");
                        console.log('ts_raw: ', ts_raw);
                        const ts = adTxtTimestampToMs(ts_raw.trim());
                        newAnnotations.push({
                            timestamp: msToTimestamp(ts),
                            text: text.trim()
                        });
                        i += 3;
                    } else {
                        i += 1;
                    }
                }
                setAnnotations(newAnnotations);
            };
            reader.readAsText(file);
        }
    };

    const handleUpdateTimestamp = () => {
        if (selectedAnnotationRow !== null) {
            const updatedAnnotations = [...annotations];
            updatedAnnotations[selectedAnnotationRow].timestamp = msToTimestamp(currentTimeMs);
            setAnnotations(updatedAnnotations);
        }
    };

    const handleSeekToTableTimestamp = (index) => {
        setSelectedAnnotationRow(index);
        const annotation = annotations[index];
        if (playerRef.current && annotation) {
            const ms = adTxtTimestampToMs(annotation.timestamp);
            playerRef.current.seekTo(ms / 1000, 'seconds');
            setIsPlaying(true);
        }
    };

    const handleAIAnnotation = async () => {
        if (!videoUrl) {
            alert('Please load a video first.');
            return;
        }

        setIsPlaying(false); // Pause the video
        setShowAIModal(true); // Open AI modal to show loading state
        setChatGptResponse(''); // Clear any previous response
        setAiError(null); // Clear any previous error

        try {
            const videoElement = videoContainerRef.current.querySelector('video');
            if (!videoElement) {
                throw new Error('Video element not found.');
            }

            const dataUrl = await htmlToImage.toPng(videoElement);
            const description = await openAiChat(dataUrl);
            setChatGptResponse(description);
        } catch (error) {
            console.error("Error capturing frame or fetching AI description:", error);
            setAiError(`Error processing AI request: ${error.message}`);
        }
    };


    return (
        <div className="flex flex-col p-4 font-sans mt-14 bg-gradient-to-br from-gray-900 to-indigo-900">
            <div className="flex flex-grow w-full gap-4">
                <div className="flex flex-col w-full lg:w-2/3 bg-white rounded-lg shadow-lg p-4 h-[600px]">
                    <div ref={videoContainerRef} className="relative bg-gray-950 rounded-lg overflow-hidden flex items-center justify-center min-h-[400px] max-h-[450px] flex-grow">
                        {videoUrl ? (
                            <ReactPlayer
                                ref={playerRef}
                                url={videoUrl}
                                playing={isPlaying}
                                controls={true}
                                width="100%"
                                height="100%"
                                onProgress={handleProgress}
                                onDuration={(d) => setDurationMs(d * 1000)}
                                playbackRate={playbackRate}
                                config={{
                                    file: {
                                        attributes: {
                                            crossOrigin: 'anonymous'
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <p className="text-white text-2xl">Load a video to begin</p>
                        )}
                        {currentCaption && (
                            <div className="absolute bottom-4 left-0 right-0 p-2 bg-black bg-opacity-70 text-yellow-300 text-lg text-center font-semibold rounded-md mx-auto max-w-xl">
                                {currentCaption}
                            </div>
                        )}
                        <div className="absolute bottom-16 right-4 p-2 bg-gray-900 bg-opacity-70 text-yellow-400 text-lg font-mono rounded-md">
                            {msToTimestamp(currentTimeMs)}
                        </div>
                    </div>

                    <input
                        type="range"
                        min="0"
                        max="1000"
                        value={progress * 1000}
                        onChange={handleSliderChange}
                        className="w-full h-2 bg-indigo-400 rounded-lg appearance-none cursor-pointer mt-4 accent-indigo-800"
                    />

                    <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                        {/* Load Video */}
                        <label htmlFor="video-upload" className="flex items-center justify-center w-40 px-4 py-2 bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-700 cursor-pointer transition-colors duration-200 text-sm">
                            <Upload className="w-5 h-5 mr-2" />
                            Load Video
                            <input id="video-upload" type="file" accept="video/*" className="hidden" onChange={handleLoadVideo} />
                        </label>

                        {/* Load SRT */}
                        <label htmlFor="srt-upload" className="flex items-center justify-center w-40 px-4 py-2 bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-700 cursor-pointer transition-colors duration-200 text-sm">
                            <FileText className="w-5 h-5 mr-2" />
                            Load SRT
                            <input id="srt-upload" type="file" accept=".srt" className="hidden" onChange={handleLoadSrt} />
                        </label>

                        {/* Load AD File */}
                        <label htmlFor="ad-file-upload" className="flex items-center justify-center w-40 px-4 py-2 bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-700 cursor-pointer transition-colors duration-200 text-sm">
                            <FileAudio className="w-5 h-5 mr-2" />
                            Load AD File
                            <input id="ad-file-upload" type="file" accept=".txt" className="hidden" onChange={handleLoadAdFile} />
                        </label>

                        {/* Play/Pause */}
                        <button
                            onClick={handleTogglePlayback}
                            className="flex items-center justify-center w-40 px-4 py-2 bg-indigo-600 text-white rounded-md shadow-md hover:bg-indigo-700 transition-colors duration-200 text-sm"
                        >
                            {isPlaying ? (
                                <>
                                    <Pause className="w-5 h-5 mr-2" />
                                    Pause
                                </>
                            ) : (
                                <>
                                    <Play className="w-5 h-5 mr-2" />
                                    Play
                                </>
                            )}
                        </button>
                        <select
                            value={playbackRate}
                            onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                            className="px-4 py-2 bg-indigo-600 text-white border-gray-300 rounded-md shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-40 text-sm"
                        >
                            {["0.25", "0.5", "0.75", "1", "1.25", "1.5", "1.75", "2"].map((rate) => (
                                <option className="px-4 py-2 bg-white text-indigo-600" key={rate} value={rate}>{rate}x</option>
                            ))}
                        </select>
                        {/* Add Annotation */}
                        <button
                            onClick={handleAddAnnotation}
                            className={`flex items-center justify-center w-40 px-4 py-2 rounded-md shadow-md transition-colors duration-200 text-sm bg-indigo-600 text-white hover:bg-indigo-700`}
                        >
                            <PlusCircle className="w-5 h-5 mr-2" />
                            Add Annotation
                        </button>

                        {/* AI Annotation */}
                        <button
                            onClick={handleAIAnnotation}
                            className={`flex items-center justify-center w-40 px-4 py-2 rounded-md shadow-lg transition-all duration-300 text-sm font-semibold ${videoUrl ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white' : 'bg-gray-400 text-gray-700 cursor-not-allowed opacity-75'}`}
                            disabled={!videoUrl}
                        >
                            <Sparkles className="w-5 h-5 mr-2" />
                            <span>AI Annotation</span>
                        </button>

                        {/* Edit Text */}
                        <button
                            onClick={handleEditAnnotationText}
                            disabled={selectedAnnotationRow === null}
                            className={`flex items-center justify-center w-40 px-4 py-2 rounded-md shadow-lg transition-colors duration-200 text-sm bg-indigo-600 text-white hover:bg-indigo-700`}
                        >
                            <Pencil className="w-5 h-5 mr-2" />
                            Edit Text
                        </button>

                        {/* Update Time */}
                        <button
                            onClick={handleUpdateTimestamp}
                            disabled={selectedAnnotationRow === null}
                            className={`flex items-center justify-center w-40 px-4 py-2 rounded-md shadow-md transition-colors duration-200 text-sm bg-indigo-600 text-white hover:bg-indigo-700`}
                        >
                            <Clock className="w-5 h-5 mr-2" />
                            Update Time
                        </button>

                        {/* Delete Selected */}
                        <button
                            onClick={handleDeleteSelectedRow}
                            className={`flex items-center justify-center w-40 px-4 py-2 rounded-md shadow-md transition-colors duration-200 text-sm bg-indigo-600 text-white hover:bg-indigo-700`}
                        >
                            <Trash2 className="w-5 h-5 mr-2" />
                            Delete Selected
                        </button>
                    </div>
                </div>

                <div className="flex flex-col w-full lg:w-1/3 overflow-auto bg-white rounded-lg shadow-lg p-4 h-[600px]">
                    <h2 className="text-xl font-bold mb-4 text-gray-800">Annotations</h2>
                    <div className="flex-grow overflow-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Timestamp
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Text
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {annotations.map((ann, index) => (
                                    <tr
                                        key={index}
                                        className={`cursor-pointer ${index === selectedAnnotationRow ? 'bg-blue-100' : 'hover:bg-gray-50'}`}
                                        onClick={() => handleSeekToTableTimestamp(index)}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {ann.timestamp}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700">
                                            {ann.text}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Save to TXT button moved here */}
                    <div className="flex justify-end mt-4">
                        <button
                            onClick={handleSaveToTxt}
                            className={`w-full flex items-center justify-center px-4 py-2 rounded-md shadow-md transition-colors duration-200 text-sm bg-indigo-600 text-white hover:bg-indigo-700`}
                        >
                            <Download className="w-5 h-5 mr-2" />
                            Save to TXT
                        </button>
                    </div>
                </div>
            </div>

            {(showAnnotationDialog || showAIModal) && (
                <AnnotationDialog
                    timestamp={msToTimestamp(currentTimeMs)}
                    initialText={showAIModal ? chatGptResponse : (annotationToEdit ? annotationToEdit.text : '')}
                    rowIndex={annotationToEdit ? annotations.indexOf(annotationToEdit) : null}
                    onClose={() => {
                        setShowAnnotationDialog(false);
                        setShowAIModal(false);
                        setChatGptResponse('');
                        setAiError(null);
                    }}
                    onSave={handleSaveAnnotation}
                    isAIModal={showAIModal}
                    isLoading={isLoadingAI}
                    error={aiError}
                />
            )}
        </div>
    );
};

export default VideoAnnotator;